import { useEffect } from "react";

/**
 * useSEO — lightweight, dependency-free per-route SEO manager.
 *
 * Updates document.head tags (title, meta description, canonical, OG,
 * Twitter) whenever the route mounts or props change. Restores the
 * previous set on unmount so tags never leak across routes.
 *
 * Because MyWorldQuest is a client-side SPA, Google/Bing rely on our
 * JS-rendered head. This hook is the SPA equivalent of what a Next.js
 * app would put in <Head>. Where possible we mirror index.html defaults
 * so the initial paint is already correct.
 *
 * Usage:
 *   useSEO({
 *     title: "Explorers Maths · Quest Academy",
 *     description: "A 20-minute-a-day mastery journey for ages 8-10…",
 *     canonical: "https://myworld.quest/academy/maths/explorers-maths",
 *     image: "https://myworld.quest/og/academy.png",
 *     type: "website" | "article" | "course",
 *   });
 *
 * JSON-LD:
 *   Pass `jsonLd={obj|obj[]}` to inject one or many structured-data blocks.
 */
const ORIGIN = "https://myworld.quest";

function setOrCreateMeta(attr, name, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

function setCanonical(href) {
  if (!href) return null;
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

function injectJsonLd(data, id) {
  if (!data) return null;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.dataset.dynamicSeo = id;
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
  return el;
}

export function useSEO({ title, description, canonical, image, type = "website", jsonLd, imageAlt } = {}) {
  const jsonLdKey = JSON.stringify(jsonLd || null);
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const restores = [];
    const jsonNodes = [];

    if (description) restores.push({ kind: "meta", ref: setOrCreateMeta("name", "description", description) });

    // Canonical
    if (canonical) restores.push({ kind: "canonical", ref: setCanonical(canonical) });

    // Open Graph
    if (title)       restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:title", title) });
    if (description) restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:description", description) });
    if (canonical)   restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:url", canonical) });
    if (type)        restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:type", type) });
    if (image)       restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:image", image) });
    if (imageAlt)    restores.push({ kind: "meta", ref: setOrCreateMeta("property", "og:image:alt", imageAlt) });

    // Twitter Card
    if (title)       restores.push({ kind: "meta", ref: setOrCreateMeta("name", "twitter:title", title) });
    if (description) restores.push({ kind: "meta", ref: setOrCreateMeta("name", "twitter:description", description) });
    if (image)       restores.push({ kind: "meta", ref: setOrCreateMeta("name", "twitter:image", image) });

    // JSON-LD blocks (all tagged with data-dynamic-seo so we can clean up)
    const items = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    items.forEach((item, i) => {
      const node = injectJsonLd(item, `route-${i}`);
      if (node) jsonNodes.push(node);
    });

    return () => {
      document.title = prevTitle;
      restores.forEach(({ ref }) => {
        if (!ref) return;
        if (ref.created) {
          try { ref.el.remove(); } catch { /* noop */ }
        } else if (ref.prev != null) {
          ref.el.setAttribute(ref.kind === "canonical" ? "href" : "content", ref.prev);
        }
      });
      jsonNodes.forEach((n) => { try { n.remove(); } catch { /* noop */ } });
    };
  }, [title, description, canonical, image, imageAlt, type, jsonLdKey, jsonLd]);
}

// ─────────────────────────────────────────────────────────────
// Small JSON-LD builders — keep the schema.org shapes in one file so a
// future audit can grep them all quickly.
// ─────────────────────────────────────────────────────────────
export function faqLd(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((q) => ({
      "@type": "Question",
      "name": q.q,
      "acceptedAnswer": { "@type": "Answer", "text": q.a },
    })),
  };
}

export function breadcrumbLd(crumbs = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": c.name,
      "item": c.href.startsWith("http") ? c.href : `${ORIGIN}${c.href}`,
    })),
  };
}

export function courseLd({ name, description, url, minAge, maxAge, image }) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "url": url?.startsWith("http") ? url : `${ORIGIN}${url}`,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "My World Quest",
      "url": ORIGIN,
    },
    "audience": {
      "@type": "EducationalAudience",
      "educationalRole": "student",
      ...(minAge != null && { "suggestedMinAge": minAge }),
      ...(maxAge != null && { "suggestedMaxAge": maxAge }),
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Online",
      "courseWorkload": "PT20M",
    },
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "category": "Free" },
    ...(image && { "image": image }),
  };
}

export function makeCanonical(path) {
  if (!path) return ORIGIN + "/";
  return path.startsWith("http") ? path : `${ORIGIN}${path.startsWith("/") ? path : "/" + path}`;
}
